import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {Helper} from '@tickchak/react-share/app';
import * as css from './TimetableSalesSummary.css';
import {formatRealtimeNumber} from '../../../../../functions';
import {COLORS} from '../../colors';
import {TTimetableSales} from '../../../../../../../redux/realtime/realtime.type';
import SummaryCard from '../SummaryCard/SummaryCard';
import Legend from '../Legend/Legend';
import Figures from '../Figures/Figures';
import MobileSummary from '../MobileSummary/MobileSummary';

type TProps = {summary: TTimetableSales['summary']; isMobile: boolean};

function TimetableSalesSummary({summary, isMobile}: TProps) {
    const {t} = useTranslation('realtime');
    const currencySymbol = useSelector((s: any) => s.event.currencySymbol);
    const {capacity, lectures, revenue} = summary;
    const money = (v: number) => `${formatRealtimeNumber(v)}${currencySymbol}`;
    const remainder = (percent: number) => Math.max(100 - percent, 0);

    if(isMobile) {
        return <MobileSummary capacity={capacity} lectures={lectures} revenue={revenue} money={money} t={t} />;
    }
    return (
        <css.summaryRow>
            <SummaryCard
                title={t('combinedRevenue')}
                wide
                segments={[
                    {value: revenue.baseTickets.total, color: COLORS.base},
                    {value: revenue.singleLecture.total, color: COLORS.casual},
                    {value: revenue.extras.total, color: COLORS.extras}
                ]}
                center={formatRealtimeNumber(revenue.total, true)}
                sub={t('total')}
                body={<Figures items={[
                    {value: money(revenue.extras.total), color: COLORS.extras, label: t('extrasAndMeals')},
                    {value: money(revenue.singleLecture.total), color: COLORS.casual, label: t('singleLectureTickets')},
                    {value: money(revenue.baseTickets.total), color: COLORS.base, label: t('baseTickets')}
                ]} />}
            />

            <SummaryCard
                title={t('capacityUtilization')}
                titleHelper={<Helper content={t('capacityHelper')} size="small" />}
                segments={[
                    {value: capacity.percent, color: COLORS.taken},
                    {value: remainder(capacity.percent), color: COLORS.free}
                ]}
                center={`${capacity.percent}%`}
                sub={t('totalSeats', {count: capacity.total})}
                body={<Legend items={[
                    {color: COLORS.free, text: t('freeSeatsCount', {count: capacity.free})},
                    {color: COLORS.taken, text: t('takenSeatsCount', {count: capacity.taken})}
                ]} />}
            />

            <SummaryCard
                title={t('lectures')}
                segments={[
                    {value: lectures.percent, color: COLORS.active},
                    {value: remainder(lectures.percent), color: COLORS.off}
                ]}
                center={`${lectures.percent}%`}
                sub={t('totalLectures', {count: lectures.total})}
                body={<Legend items={[
                    {color: COLORS.off, text: t('offLecturesCount', {count: lectures.off})},
                    {color: COLORS.active, text: t('activeLecturesCount', {count: lectures.active})}
                ]} />}
            />
        </css.summaryRow>
    );
}
export default TimetableSalesSummary;
